import AsyncStorage from '@react-native-async-storage/async-storage';
import { calendarSyncService } from '@/lib/services/CalendarSyncService';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';
import { summaryScheduler } from '@/lib/services/SummaryScheduler';
import { FamilyContextCollector } from '@/lib/family/FamilyContextCollector';
import type { LocalFamilyMembersStore } from '@/lib/local/LocalFamilyMembersStore';
import type { LocalCalendarMemberMappingStore } from '@/lib/local/LocalCalendarMemberMappingStore';
import type { HomeSummary, SummaryPreferences, SummaryVariant } from '@/types/home-summary';

interface LoadSummaryParams {
  userId: string;
  preferences: SummaryPreferences;
  today: string;
  forceRegenerate?: boolean;
  onSyncingChange?: (isSyncing: boolean) => void;
  onRefreshStarted?: () => void;
  onSummaryUpdated?: (summary: HomeSummary) => void;
  familyMembersStore?: LocalFamilyMembersStore | null;
  calendarMappingStore?: LocalCalendarMemberMappingStore | null;
  latitude?: number;
  longitude?: number;
}

interface RegenerateParams {
  userId: string;
  preferences: SummaryPreferences;
  today: string;
  onSyncingChange?: (isSyncing: boolean) => void;
  familyMembersStore?: LocalFamilyMembersStore | null;
  calendarMappingStore?: LocalCalendarMemberMappingStore | null;
  latitude?: number;
  longitude?: number;
}

/**
 * HomeScreenManager handles business logic for the home screen.
 * Manages summary preferences, loading summaries, and calendar synchronization.
 */
export class HomeScreenManager {
  private static instance: HomeScreenManager | null = null;

  static getInstance(): HomeScreenManager {
    if (!HomeScreenManager.instance) {
      HomeScreenManager.instance = new HomeScreenManager();
    }
    return HomeScreenManager.instance;
  }

  private isSummaryStale(
    summary: HomeSummary,
    today: string,
    expectedVariant: SummaryVariant | null,
  ): boolean {
    if (summary.targetDate !== today) return true;
    if (expectedVariant && summary.variant !== expectedVariant) return true;
    return false;
  }

  determineVariant(prefs: SummaryPreferences): SummaryVariant | null {
    const { morningBrief, eveningRecap } = prefs;
    const hour = new Date().getHours();

    if (hour < 15) {
      if (morningBrief.enabled) return 'morning-brief';
      if (eveningRecap.enabled) return 'evening-recap';
    } else {
      if (eveningRecap.enabled) return 'evening-recap';
      if (morningBrief.enabled) return 'morning-brief';
    }

    return null;
  }

  async fetchPreferences(): Promise<SummaryPreferences | null> {
    try {
      console.log('[HomeScreenManager] Fetching preferences');

      const response = await authenticatedFetch.fetch('/api/home-summary/preferences');

      if (!response.ok) {
        throw new Error(`Failed to load summary preferences (${response.status})`);
      }

      const data = (await response.json()) as { preferences: SummaryPreferences };

      summaryScheduler
        .syncPreferences(data.preferences)
        .catch((err) => console.error('Failed to sync summary preferences', err));

      return data.preferences;
    } catch (err) {
      console.error('[HomeScreenManager] Failed to load summary preferences:', err);
      throw err;
    }
  }

  /**
   * Load or generate a home summary.
   * Returns stale summary immediately while regenerating in background.
   */
  async loadSummary(params: LoadSummaryParams): Promise<HomeSummary | null> {
    const { today, forceRegenerate, onRefreshStarted, onSummaryUpdated, preferences } = params;
    const expectedVariant = this.determineVariant(preferences);

    try {
      if (!forceRegenerate) {
        const cached = await this.fetchCachedSummary();

        if (cached && !this.isSummaryStale(cached, today, expectedVariant)) {
          console.log('[HomeScreenManager] Summary is fresh, returning cached');
          return cached;
        }

        if (cached) {
          console.log(
            `[HomeScreenManager] Summary is stale (cached variant=${cached.variant}/${cached.targetDate}, expected=${expectedVariant}/${today}), returning cached + background refresh`,
          );
          onRefreshStarted?.();
          this.regenerateSummary(params)
            .then((fresh) => { if (fresh) onSummaryUpdated?.(fresh); })
            .catch((err) => {
              console.error('[HomeScreenManager] Background refresh failed:', err);
              // Signal refresh ended even on failure so UI can clear loading state
              onSummaryUpdated?.(cached);
            });
          return cached;
        }

        console.log('[HomeScreenManager] No existing summary, generating new one');
      }

      return await this.regenerateSummary(params);
    } catch (err) {
      console.error('[HomeScreenManager] Failed to load/generate summary:', err);
      throw err;
    }
  }

  async refreshSummary(params: {
    userId: string;
    preferences: SummaryPreferences | null;
    today: string;
    onSyncingChange?: (isSyncing: boolean) => void;
    onSummaryUpdated?: (summary: HomeSummary) => void;
    familyMembersStore?: LocalFamilyMembersStore | null;
    calendarMappingStore?: LocalCalendarMemberMappingStore | null;
  }): Promise<HomeSummary | null> {
    const { preferences } = params;

    if (!preferences) {
      const prefs = await this.fetchPreferences();
      if (!prefs) return null;

      return this.loadSummary({ ...params, preferences: prefs, forceRegenerate: true });
    }

    return this.loadSummary({ ...params, preferences, forceRegenerate: true });
  }

  private async fetchCachedSummary(): Promise<HomeSummary | null> {
    const response = await authenticatedFetch.fetch('/api/home-summary');
    if (!response.ok) return null;

    const data = (await response.json()) as { summary: HomeSummary | null };
    return data.summary ?? null;
  }

  private async regenerateSummary(params: RegenerateParams): Promise<HomeSummary | null> {
    const { userId, preferences, today, onSyncingChange } = params;

    const variant = this.determineVariant(preferences);
    if (!variant) return null;

    const calendarIds = await this.loadSelectedCalendarIds();

    // Sync calendars for fresh data
    console.log('[HomeScreenManager] Syncing calendars before generation...');
    onSyncingChange?.(true);

    const syncResult = await calendarSyncService.syncForSummary(userId, variant, today, calendarIds);

    if (!syncResult.success) {
      console.warn('[HomeScreenManager] Calendar sync failed:', syncResult.error);
      throw new Error(`Calendar sync failed: ${syncResult.error}`);
    }

    console.log(`[HomeScreenManager] Calendar sync: ${syncResult.syncedCount} events synced`);
    onSyncingChange?.(false);

    // Collect family context
    const familyContext = await FamilyContextCollector.collect(
      params.familyMembersStore ?? null,
      params.calendarMappingStore ?? null,
    );

    // Generate summary via API
    console.log('[HomeScreenManager] Generating summary...');
    const response = await authenticatedFetch.fetch('/api/home-summary', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        variant,
        calendarIds,
        targetDate: today,
        ...(familyContext && {
          familyMembers: familyContext.familyMembers,
          familyMemberNotes: familyContext.familyMemberNotes,
          calendarMemberMappings: familyContext.calendarMemberMappings,
          meFamilyMemberId: familyContext.meFamilyMemberId,
        }),
        ...(params.latitude != null && params.longitude != null && {
          latitude: params.latitude,
          longitude: params.longitude,
        }),
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[HomeScreenManager] API error:', errorText);
      throw new Error(`Failed to generate summary (${response.status})`);
    }

    const data = (await response.json()) as { summary: HomeSummary | null };
    console.log('[HomeScreenManager] ✓ Summary generated successfully');

    return data.summary ?? null;
  }

  private async loadSelectedCalendarIds(): Promise<string[] | undefined> {
    try {
      const savedSelection = await AsyncStorage.getItem('selectedCalendarIds');
      return savedSelection ? JSON.parse(savedSelection) : undefined;
    } catch (e) {
      console.warn('[HomeScreenManager] Failed to load selected calendar IDs', e);
      return undefined;
    }
  }
}
